import type {
  AutorizacaoDoEnvioFisicoArDto,
  DocumentoDoEnvioFisicoArDto,
  EnderecoDoEnvioFisicoArDto,
  EnvioFisicoArAcompanhamento,
  EnvioFisicoArAviso,
  EnvioFisicoArDocumento,
  EnvioFisicoArDocumentoTipo,
  EnvioFisicoArDto,
  EnvioFisicoArEvento,
  EnvioFisicoArStatus,
  EtapaDoEnvioFisicoArDto,
  EtapaEnvioFisicoAr,
  MotivoDocumentoIndisponivelAr,
  PostalDoEnvioFisicoArDto,
  StatusCorreiosAr,
} from '../types/envioFisicoArTypes';

/**
 * Traduz o DTO da API para o modelo que o card da designer desenha.
 *
 * O card é contrato (regra #13), então os textos vêm do mock sempre que ele tinha o caso.
 * O mock misturava os dois eixos — "a empresa recusou" saía como "o destinatário recusou" —
 * e aqui cada eixo tem os seus. Onde o mock não tinha o estado (entregue, devolvido,
 * prazo de autorização vencido...), o texto foi escrito agora e está listado no PR para a
 * designer aprovar.
 */

export const STATUS_LABEL: Record<EnvioFisicoArStatus, string> = {
  AGUARDANDO_AUTORIZACAO: 'Aguardando autorização',
  RECUSADO_PELO_REQUERENTE: 'Recusado pelo requerente',
  ENDERECO_EM_CORRECAO: 'Endereço em correção',
  PRAZO_AUTORIZACAO_VENCIDO: 'Prazo vencido sem resposta',
  AGUARDANDO_POSTAGEM: 'Aguardando postagem',
  EM_PRODUCAO: 'Em produção',
  POSTADO: 'Postado',
  EM_TRANSITO: 'Em trânsito',
  SAIU_PARA_ENTREGA: 'Saiu para entrega',
  ENTREGUE: 'Entregue',
  RECUSADO: 'Envio recusado',
  // O mock usava "Endereço em correção", que é também o rótulo da devolução do endereço
  // pelo requerente. Com os dois eixos no mesmo card, o mesmo texto diria duas coisas.
  ENDERECO_INVALIDO: 'Endereço não localizado',
  AGUARDANDO_RETORNO: 'Aguardando retorno',
  ATUALIZANDO_RASTREIO: 'Atualizando rastreio',
  AUSENTE: 'Destinatário ausente',
  DEVOLVIDO: 'Devolvido',
  CANCELADO: 'Cancelado',
};

function situacaoDaAutorizacao(autorizacao: AutorizacaoDoEnvioFisicoArDto): EnvioFisicoArStatus {
  switch (autorizacao.status) {
    case 'RECUSADA':
      return 'RECUSADO_PELO_REQUERENTE';
    case 'ENDERECO_EM_CORRECAO':
      return 'ENDERECO_EM_CORRECAO';
    case 'PRAZO_VENCIDO':
      return 'PRAZO_AUTORIZACAO_VENCIDO';
    case 'AUTORIZADA':
      return 'AGUARDANDO_POSTAGEM';
    // Inalcançável hoje (o back não lista CANCELADA sem envio), mas explícita: no `default` ela
    // virava "Aguardando autorização", contradizendo a timeline.
    case 'CANCELADA':
      return 'CANCELADO';
    case 'AGUARDANDO_AUTORIZACAO':
      return 'AGUARDANDO_AUTORIZACAO';
  }
}

/** Com os Correios e ainda sem desfecho: é onde o prazo de retorno e o rastreio pesam. */
const STATUS_COM_OS_CORREIOS: ReadonlySet<StatusCorreiosAr> = new Set<StatusCorreiosAr>([
  'POSTADO',
  'EM_TRANSITO',
  'SAIU_PARA_ENTREGA',
]);

/**
 * Situação postal, na precedência da galeria da designer: prazo de retorno vencido sem
 * desfecho vira "Aguardando retorno", e postagem confirmada sem código vira "Atualizando
 * rastreio" — nos dois o status cru dos Correios contaria menos do que está acontecendo.
 */
function situacaoPostal(postal: PostalDoEnvioFisicoArDto): EnvioFisicoArStatus {
  if (postal.status === 'PENDENTE') return 'AGUARDANDO_POSTAGEM';
  if (!STATUS_COM_OS_CORREIOS.has(postal.status)) return postal.status;
  if (postal.statusPrazo === 'VENCIDO') return 'AGUARDANDO_RETORNO';
  if (!postal.codigoRastreio) return 'ATUALIZANDO_RASTREIO';
  return postal.status;
}

/** Com envio, é o eixo postal que diz onde a carta está; sem ele, a decisão do requerente. */
function situacaoDoEnvio(item: EnvioFisicoArDto): EnvioFisicoArStatus {
  if (item.postal) return situacaoPostal(item.postal);
  return item.autorizacao ? situacaoDaAutorizacao(item.autorizacao) : 'AGUARDANDO_AUTORIZACAO';
}

/** O que os textos precisam saber além da própria etapa. */
interface ContextoDoCard {
  situacao: EnvioFisicoArStatus;
  codigoRastreio: string | null;
  arDisponivel: boolean;
}

/** `YYYY-MM-DD` → `DD/MM/AAAA`, sem passar por `Date` (que deslocaria o dia pelo fuso). */
function dataDeCalendario(diaIso: string): string {
  const [ano, mes, dia] = diaIso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

const AVISO_AGUARDANDO_AUTORIZACAO: EnvioFisicoArAviso = {
  texto:
    'O prazo de retorno começa a contar somente depois que a empresa autorizar e a carta for postada.',
  variante: 'brand',
};

const AVISO_AUTORIZADO_SEM_POSTAGEM: EnvioFisicoArAviso = {
  texto: 'O envio foi autorizado. O prazo de retorno começa a contar somente após a postagem.',
  variante: 'brand',
};

/**
 * Textos da galeria de estados da designer (handoff "Envio físico de AR — documentação
 * visual", seção 5). Dois foram ajustados para não afirmar o que o sistema não faz — ver
 * `ATUALIZANDO_RASTREIO` e `avisoDaEntrega`.
 */
const AVISO_POR_SITUACAO: Partial<Record<EnvioFisicoArStatus, EnvioFisicoArAviso>> = {
  AGUARDANDO_AUTORIZACAO: AVISO_AGUARDANDO_AUTORIZACAO,
  AGUARDANDO_POSTAGEM: AVISO_AUTORIZADO_SEM_POSTAGEM,
  EM_PRODUCAO: AVISO_AUTORIZADO_SEM_POSTAGEM,
  RECUSADO_PELO_REQUERENTE: {
    texto:
      'O envio desta carta foi recusado. A secretaria foi informada e definirá o próximo passo.',
    variante: 'error',
  },
  ENDERECO_EM_CORRECAO: {
    texto:
      'O endereço foi devolvido para correção. A secretaria enviará uma nova solicitação com o endereço corrigido.',
    // `error` como no mock (regra #13); só o texto mudou, porque o do mock falava dos Correios.
    variante: 'error',
  },
  PRAZO_AUTORIZACAO_VENCIDO: {
    texto:
      'O prazo para autorizar este envio terminou sem resposta. A secretaria foi informada e definirá o próximo passo.',
    variante: 'warning',
  },
  POSTADO: {
    texto: 'A postagem foi confirmada. O histórico será atualizado na próxima movimentação.',
    variante: 'brand',
  },
  SAIU_PARA_ENTREGA: {
    texto: 'A entrega está prevista para hoje. O resultado será informado pelos Correios.',
    variante: 'brand',
  },
  AGUARDANDO_RETORNO: {
    texto:
      'O prazo passou sem resultado final. O histórico conhecido continua disponível para a secretaria acompanhar.',
    variante: 'warning',
  },
  // A galeria dizia "a tela preserva a data e tenta atualizar", mas a aba não se atualiza
  // sozinha: o código aparece na próxima vez que ela for aberta.
  ATUALIZANDO_RASTREIO: {
    texto:
      'A postagem foi confirmada, mas o código ainda não foi recebido. A data da postagem fica registrada e o código aparece aqui assim que os Correios o enviarem.',
    variante: 'brand',
  },
  RECUSADO: {
    texto:
      'O destinatário recusou o recebimento. A secretaria foi informada e definirá o próximo passo.',
    variante: 'error',
  },
  ENDERECO_INVALIDO: {
    texto:
      'Os Correios não localizaram o endereço. A secretaria foi informada para revisar os dados antes de uma nova tentativa.',
    variante: 'error',
  },
  AUSENTE: {
    texto: 'A tentativa não foi concluída porque não havia pessoa disponível para receber.',
    variante: 'warning',
  },
  DEVOLVIDO: {
    texto:
      'O objeto retornou sem entrega. A secretaria avaliará nova tentativa ou outro meio de citação.',
    variante: 'error',
  },
  CANCELADO: {
    texto: 'Este envio foi cancelado. Nenhuma nova ação é necessária por enquanto.',
    variante: 'warning',
  },
};

/** Carta com os Correios e ainda sem retorno: o aviso é o prazo de retorno do AR. */
function avisoDoPrazo(postal: PostalDoEnvioFisicoArDto): EnvioFisicoArAviso {
  const acompanhamento =
    'O histórico será atualizado conforme os Correios registrarem novas movimentações.';
  if (!postal.prazoRetorno) return { texto: acompanhamento, variante: 'brand' };
  return {
    texto: `Previsão de retorno até ${dataDeCalendario(postal.prazoRetorno)}. ${acompanhamento}`,
    variante: 'brand',
  };
}

/**
 * Entrega ≠ AR recebido (AC-H3-04): com o AR ainda pendente, o aviso não afirma que a prova
 * chegou. A galeria dizia "a foto pode estar disponível antes do AR", mas a foto nunca vem —
 * o provedor não a entrega (AC-H3-05) —, então a frase fala só do AR.
 */
function avisoDaEntrega(arDisponivel: boolean): EnvioFisicoArAviso {
  return {
    texto: arDisponivel
      ? 'A entrega foi concluída e o AR efetivamente recebido está disponível para consulta.'
      : 'A entrega foi confirmada. O AR efetivamente recebido fica disponível assim que os Correios o processarem.',
    variante: 'success',
  };
}

/**
 * req-ar-006: "No convênio, informa que a postagem/cobrança segue o fluxo do convênio" — e o
 * cenário de QA 2 pede o aviso de convênio. Sem ele, convênio e avulso autorizados ficavam
 * idênticos, e quem é do convênio procurava um pagamento que não existe.
 */
const AVISO_CONVENIO_SEM_POSTAGEM: EnvioFisicoArAviso = {
  texto:
    'O envio foi autorizado pelo convênio e o valor entra na fatura do ciclo. O prazo de retorno começa a contar somente após a postagem.',
  variante: 'brand',
};

/** Envio dos fluxos antigos nasceu sem autorização: dizer "foi autorizado" seria inventar. */
/** Com o selo "Em produção", dizer "a carta foi preparada" contradiz o próprio card. */
const AVISO_CARTA_EM_PRODUCAO: EnvioFisicoArAviso = {
  texto: 'A carta está em produção. O prazo de retorno começa a contar somente após a postagem.',
  variante: 'brand',
};

const AVISO_CARTA_PREPARADA: EnvioFisicoArAviso = {
  texto: 'A carta foi preparada. O prazo de retorno começa a contar somente após a postagem.',
  variante: 'brand',
};

function avisoDoEnvio(item: EnvioFisicoArDto, contexto: ContextoDoCard): EnvioFisicoArAviso {
  const { situacao } = contexto;
  const antesDaPostagem = situacao === 'AGUARDANDO_POSTAGEM' || situacao === 'EM_PRODUCAO';
  if (antesDaPostagem && !item.autorizacao) {
    return situacao === 'EM_PRODUCAO' ? AVISO_CARTA_EM_PRODUCAO : AVISO_CARTA_PREPARADA;
  }
  if (antesDaPostagem && item.autorizacao?.cobranca === 'CONVENIO') {
    return AVISO_CONVENIO_SEM_POSTAGEM;
  }
  if (situacao === 'ENTREGUE') return avisoDaEntrega(contexto.arDisponivel);
  const fixo = AVISO_POR_SITUACAO[situacao];
  if (fixo) return fixo;
  return item.postal ? avisoDoPrazo(item.postal) : AVISO_AGUARDANDO_AUTORIZACAO;
}

type TextoDaEtapa = Pick<EnvioFisicoArEvento, 'titulo' | 'descricao'>;

function textoDaAutorizacao(etapa: EtapaDoEnvioFisicoArDto): TextoDaEtapa {
  if (etapa.estado === 'CONCLUIDO') {
    return {
      titulo: 'Autorização da empresa',
      descricao:
        'A empresa autorizou o envio. A cobrança foi confirmada e a carta seguiu para preparação.',
    };
  }
  if (etapa.desfecho === 'RECUSADA') {
    return {
      titulo: 'Envio recusado',
      descricao:
        'A autorização não foi concedida. A secretaria foi avisada para avaliar o próximo passo.',
    };
  }
  if (etapa.desfecho === 'ENDERECO_EM_CORRECAO') {
    return {
      titulo: 'Endereço precisa de correção',
      descricao:
        'A postagem está pausada até que a secretaria envie uma nova solicitação com o endereço corrigido.',
    };
  }
  if (etapa.desfecho === 'CANCELADA') {
    return { titulo: 'Envio cancelado', descricao: 'Histórico preservado.' };
  }
  if (etapa.desfecho === 'PRAZO_VENCIDO') {
    return {
      titulo: 'Prazo de autorização encerrado',
      descricao:
        'A autorização não foi dada dentro do prazo. A secretaria foi avisada para avaliar o próximo passo.',
    };
  }
  return {
    titulo: 'Autorização da empresa',
    descricao: 'Aguardando a aprovação da empresa para liberar a postagem.',
  };
}

function textoDaProducao(etapa: EtapaDoEnvioFisicoArDto): TextoDaEtapa {
  const descricao: Record<EtapaDoEnvioFisicoArDto['estado'], string> = {
    CONCLUIDO: 'A carta foi produzida e está pronta para coleta dos Correios.',
    ATUAL: 'A carta será produzida antes de ser entregue aos Correios.',
    PENDENTE: 'A produção começa depois que a autorização for concluída.',
    ATENCAO: 'A produção começa depois que a autorização for concluída.',
  };
  return { titulo: 'Carta preparada', descricao: descricao[etapa.estado] };
}

function textoDaPostagem(etapa: EtapaDoEnvioFisicoArDto, contexto: ContextoDoCard): TextoDaEtapa {
  if (etapa.estado !== 'CONCLUIDO') {
    return {
      titulo: 'Postagem registrada',
      descricao: 'O código de rastreio aparecerá aqui depois da postagem.',
    };
  }
  return {
    titulo: 'Postagem registrada',
    descricao: contexto.codigoRastreio
      ? 'A carta foi entregue aos Correios e recebeu o código de rastreio.'
      : 'Código pendente. Aguardando retorno dos Correios.',
  };
}

function textoDoTransito(etapa: EtapaDoEnvioFisicoArDto, contexto: ContextoDoCard): TextoDaEtapa {
  if (etapa.estado === 'PENDENTE') {
    return {
      titulo: 'Em trânsito',
      descricao: 'O acompanhamento do trajeto começa depois que a carta for postada.',
    };
  }
  if (etapa.estado === 'CONCLUIDO') {
    return { titulo: 'Em trânsito', descricao: 'A carta concluiu o trajeto dos Correios.' };
  }
  if (contexto.situacao === 'AGUARDANDO_RETORNO') {
    return { titulo: 'Sem nova movimentação', descricao: 'Prazo esperado encerrado.' };
  }
  if (contexto.situacao === 'SAIU_PARA_ENTREGA') {
    return { titulo: 'Saiu para entrega', descricao: 'Objeto com o carteiro.' };
  }
  // A última movimentação é dado real dos Correios e diz mais do que o texto genérico.
  if (etapa.detalhe) return { titulo: 'Em trânsito', descricao: etapa.detalhe };
  // Só postada, sem movimentação: "os Correios atualizaram o trajeto" afirmaria um evento
  // que não houve.
  if (contexto.situacao === 'POSTADO') {
    return { titulo: 'Em trânsito', descricao: 'Aguardando a primeira movimentação dos Correios.' };
  }
  return {
    titulo: 'Em trânsito',
    descricao:
      'Os Correios atualizaram o trajeto da carta. O próximo evento é a entrega no endereço informado.',
  };
}

/** Ocorrências ganham título próprio na timeline, como na galeria da designer. */
const RESULTADO_POR_DESFECHO: Partial<
  Record<NonNullable<EtapaDoEnvioFisicoArDto['desfecho']>, TextoDaEtapa>
> = {
  RECUSADO: {
    titulo: 'Resultado do AR',
    descricao: 'O destinatário recusou o recebimento da carta.',
  },
  ENDERECO_INVALIDO: {
    titulo: 'Resultado do AR',
    descricao: 'Os Correios não localizaram o endereço informado.',
  },
  AUSENTE: { titulo: 'Destinatário ausente', descricao: 'Nova movimentação aguardada.' },
  DEVOLVIDO: { titulo: 'Objeto devolvido', descricao: 'Envio voltou à origem.' },
  CANCELADO: { titulo: 'Envio cancelado', descricao: 'Histórico preservado.' },
};

function textoDoResultado(etapa: EtapaDoEnvioFisicoArDto, contexto: ContextoDoCard): TextoDaEtapa {
  if (etapa.desfecho === 'ENTREGUE') {
    return {
      titulo: 'Resultado do AR',
      descricao: contexto.arDisponivel
        ? 'Objeto entregue. Documento processado.'
        : 'Objeto entregue. Processando o AR efetivamente recebido.',
    };
  }
  const ocorrencia = etapa.desfecho ? RESULTADO_POR_DESFECHO[etapa.desfecho] : undefined;
  if (ocorrencia) return ocorrencia;
  // Sem prometer a foto dos Correios: o provedor não a entrega (AC-H3-05).
  return {
    titulo: 'Resultado do AR',
    descricao: 'O AR efetivamente recebido fica disponível assim que os Correios o processarem.',
  };
}

function textoDaSolicitacao(): TextoDaEtapa {
  return {
    titulo: 'Solicitação criada',
    descricao: 'A secretaria solicitou o envio físico do AR para o destinatário.',
  };
}

type TextoPorEtapa = (etapa: EtapaDoEnvioFisicoArDto, contexto: ContextoDoCard) => TextoDaEtapa;

const TEXTO_DA_ETAPA: Record<EtapaEnvioFisicoAr, TextoPorEtapa> = {
  SOLICITACAO: textoDaSolicitacao,
  AUTORIZACAO: textoDaAutorizacao,
  PRODUCAO: textoDaProducao,
  POSTAGEM: textoDaPostagem,
  TRANSITO: textoDoTransito,
  RESULTADO: textoDoResultado,
};

/**
 * `null` para etapa que este front não conhece. O mapeamento roda no selector, lido pelo
 * container em TODA renderização do processo: uma etapa nova vinda do back derrubaria a
 * página inteira, e não só a aba. Ela some da timeline até o front aprender o texto dela.
 */
function eventoDaEtapa(
  itemId: string,
  etapa: EtapaDoEnvioFisicoArDto,
  contexto: ContextoDoCard,
): EnvioFisicoArEvento | null {
  if (!Object.hasOwn(TEXTO_DA_ETAPA, etapa.etapa)) return null;
  return {
    id: `${itemId}-${etapa.etapa.toLowerCase()}`,
    ...TEXTO_DA_ETAPA[etapa.etapa](etapa, contexto),
    ocorridoEm: etapa.ocorridoEm,
    status: etapa.estado,
  };
}

const TITULO_DO_DOCUMENTO: Record<EnvioFisicoArDocumentoTipo, string> = {
  CARTA_ENVIADA: 'Carta enviada',
  FOTO_CORREIOS: 'Foto enviada pelos Correios',
  AR_EFETIVAMENTE_RECEBIDO: 'AR efetivamente recebido',
};

const DESCRICAO_DO_DOCUMENTO: Record<EnvioFisicoArDocumentoTipo, string> = {
  CARTA_ENVIADA: 'Cópia da carta que foi postada para o destinatário.',
  FOTO_CORREIOS: 'Imagem registrada pelos Correios no momento da entrega ou tentativa.',
  AR_EFETIVAMENTE_RECEBIDO:
    'Comprovante final com o resultado da entrega e a assinatura do destinatário.',
};

const MOTIVO_INDISPONIVEL: Record<MotivoDocumentoIndisponivelAr, string> = {
  CARTA_NAO_PRODUZIDA: 'Disponível depois que a carta for produzida e postada.',
  NAO_FORNECIDO_PELO_PROVEDOR: 'Os Correios não fornecem esta imagem para este envio.',
  AR_NAO_RETORNADO: 'Disponível quando os Correios devolverem o AR processado.',
};

/**
 * `null` para documento que este front não conhece, pelo mesmo motivo de `eventoDaEtapa`: o
 * título `undefined` quebraria o `aria-label` da linha e, com ele, a página do processo. Core
 * e front sobem em deploys separados, e um core fora de passo manda tipo que o front não tem
 * (o `CONTEUDO_CARTA`, que saiu do card em 86akqymtq).
 */
function documentoDoEnvio(documento: DocumentoDoEnvioFisicoArDto): EnvioFisicoArDocumento | null {
  if (!Object.hasOwn(TITULO_DO_DOCUMENTO, documento.tipo)) return null;
  return {
    id: documento.tipo.toLowerCase(),
    tipo: documento.tipo,
    titulo: TITULO_DO_DOCUMENTO[documento.tipo],
    descricao: documento.motivoIndisponivel
      ? MOTIVO_INDISPONIVEL[documento.motivoIndisponivel]
      : DESCRICAO_DO_DOCUMENTO[documento.tipo],
    disponibilidade: documento.disponivel ? 'DISPONIVEL' : 'INDISPONIVEL',
    nomeArquivo: documento.nomeArquivo,
  };
}

function enderecoEmLinha(endereco: EnderecoDoEnvioFisicoArDto | null): string {
  if (!endereco) return '';
  const complemento = endereco.complemento ? `, ${endereco.complemento}` : '';
  return `${endereco.logradouro}, ${endereco.numero}${complemento} · ${endereco.bairro}, ${endereco.cidade}/${endereco.uf}`;
}

/**
 * @example const card = comoAcompanhamentoDoAr(processoId, dto);
 */
export function comoAcompanhamentoDoAr(
  processoArbitralId: string,
  item: EnvioFisicoArDto,
): EnvioFisicoArAcompanhamento {
  const situacao = situacaoDoEnvio(item);
  const contexto: ContextoDoCard = {
    situacao,
    codigoRastreio: item.postal?.codigoRastreio ?? null,
    arDisponivel: item.documentos.some(
      (d) => d.tipo === 'AR_EFETIVAMENTE_RECEBIDO' && d.disponivel,
    ),
  };
  return {
    id: item.id,
    processoArbitralId,
    destinatario: item.destinatarioNome ?? 'Destinatário não identificado',
    endereco: enderecoEmLinha(item.endereco),
    tentativa: item.tentativa,
    codigoRastreio: item.postal?.codigoRastreio ?? null,
    status: situacao,
    statusLabel: STATUS_LABEL[situacao],
    atualizadaEm: item.postal?.atualizadoEm ?? null,
    aviso: avisoDoEnvio(item, contexto),
    eventos: item.etapas.flatMap((etapa) => eventoDaEtapa(item.id, etapa, contexto) ?? []),
    documentos: item.documentos.flatMap((documento) => documentoDoEnvio(documento) ?? []),
  };
}
